import { Localized, useLocalization } from "@fluent/react"
import { Box, Button, Checkbox, Chip, Collapse, FormControlLabel, InputAdornment, MenuItem, Stack, TextField } from "@mui/material"
import SearchIcon from '@mui/icons-material/Search'
import FilterAltIcon from '@mui/icons-material/FilterAlt'
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { ReactNode, useCallback, useEffect, useRef, useState } from "react"
import { useDebouncedValue } from "../../../app/hooks"
import { useLocalStorage } from "../../../useLocalStorage"
import { CommentsFilter, emptyRequirementFilter, isFilterActive, RequirementFilter } from "../requirementFilter"

export interface RequirementFilterBarProps {
    filter: RequirementFilter
    onChange: (filter: RequirementFilter) => void
    // Across the whole document, not just the visible tab — the pager crosses tabs.
    matchCount: number
    totalCount: number
    showSupplierNote: boolean
    // Reordering writes positions computed from the unfiltered tree, so the two
    // modes cannot be active at once. Reorder wins: it holds unsaved changes.
    disabled: boolean
}

// One entry per option: the menu item renders it through <Localized>, the
// collapsed value through l10n.getString, so both stay in the same place and
// cannot drift.
interface FacetOption {
    value: string
    id: string
    fallback: string
}

const CRITICALITIES: FacetOption[] = [
    { value: 'E', id: 'urs-item-criticality-e', fallback: 'Essential' },
    { value: 'D', id: 'urs-item-criticality-d', fallback: 'Desired' },
    { value: 'I', id: 'urs-item-criticality-i', fallback: 'Optional' },
]

const STATUSES: FacetOption[] = [
    { value: 'ADDED', id: 'urs-filter-status-added', fallback: 'Added' },
    { value: 'MODIFIED', id: 'urs-filter-status-modified', fallback: 'Modified' },
    { value: 'DELETED', id: 'urs-filter-status-deleted', fallback: 'Deleted' },
]

const NOTES: FacetOption[] = [
    { value: 'F', id: 'urs-offer-req-f', fallback: 'Fulfills' },
    { value: 'U', id: 'urs-offer-req-u', fallback: 'Unfulfilled' },
    { value: 'D', id: 'urs-offer-req-d', fallback: 'To be discussed' },
]

interface FacetSelectProps {
    label: ReactNode
    options: FacetOption[]
    selected: Set<string>
    onChange: (values: Set<string>) => void
    disabled: boolean
}

const FacetSelect = (props: FacetSelectProps) => {
    const { label, options, selected, onChange, disabled } = props
    const { l10n } = useLocalization()

    // renderValue takes a ReactNode, so the collapsed value can carry the real
    // labels; l10n.getString gives them as plain strings for joining. Ellipsis
    // rather than wrapping, so a multi-selection can't grow the toolbar row.
    const renderValue = (raw: unknown) => {
        const values = raw as string[]
        if (values.length === 0) return ''
        const labels = values.map(v => {
            const option = options.find(o => o.value === v)
            return option === undefined ? v : l10n.getString(option.id, null, option.fallback)
        })
        return <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {labels.join(', ')}
        </Box>
    }

    return <TextField
        size="small"
        select
        sx={{ width: 200 }}
        disabled={disabled}
        value={Array.from(selected)}
        onChange={e => onChange(new Set(e.target.value as unknown as string[]))}
        label={label}
        SelectProps={{ multiple: true, renderValue, displayEmpty: true }}
        InputLabelProps={{ shrink: true }}
    >
        {options.map(o => <MenuItem key={o.value} value={o.value}>
            <Checkbox size="small" sx={{ py: 0, pl: 0 }} checked={selected.has(o.value)} />
            <Localized id={o.id}><span>{o.fallback}</span></Localized>
        </MenuItem>)}
    </TextField>
}

// Remembered per user: the controls cost a row of vertical space above a table
// that is already tall, and whether that trade is worth it depends on whether
// you filter habitually.
const expandedStorageKey = 'UrsRequirementFilter_Expanded'

export const RequirementFilterBar = (props: RequirementFilterBarProps) => {
    const { filter, onChange, matchCount, totalCount, showSupplierNote, disabled } = props
    const [expanded, setExpanded] = useLocalStorage(expandedStorageKey, false)

    // The search box keeps its own text and only pushes it up once typing settles.
    // Not for the filtering itself — that is sub-millisecond — but because every
    // change to `filter` re-renders the whole table, and the row list is rebuilt
    // through a SortableContext whose items array is compared by identity, so each
    // keystroke would re-render every row on screen.
    const [text, setText] = useState(filter.text)
    const debouncedText = useDebouncedValue(text, 300)
    // What we last sent up, so the echo of our own propagation is distinguishable
    // from a reset made elsewhere. Only the latter should overwrite what the user
    // is typing. This cannot see a reset whose text matches what we last sent —
    // clearing a filter whose text is already '' looks identical to no change at
    // all — so Clear below resets `text` itself rather than relying on the echo.
    const propagatedRef = useRef(filter.text)
    if (filter.text !== propagatedRef.current) {
        propagatedRef.current = filter.text
        setText(filter.text)
    }

    const handleClear = useCallback(() => {
        setText('')
        onChange(emptyRequirementFilter)
    }, [onChange])

    useEffect(() => {
        if (debouncedText === filter.text) return
        // A debounced value the box no longer holds is the tail of a burst that has
        // since been cleared or typed past; the next tick carries the current text.
        if (debouncedText !== text) return
        propagatedRef.current = debouncedText
        onChange({ ...filter, text: debouncedText })
    }, [debouncedText, text, filter, onChange])

    // Pending text counts as active so Clear stays reachable during the debounce.
    const active = isFilterActive(filter) || text.trim() !== ''

    const handleSet = useCallback(<K extends keyof RequirementFilter>(key: K, value: RequirementFilter[K]) => {
        onChange({ ...filter, [key]: value })
    }, [filter, onChange])

    const handleSetChange = useCallback((key: 'criticality' | 'status' | 'note', values: Set<string>) => {
        onChange({ ...filter, [key]: values })
    }, [filter, onChange])

    return <Stack direction="column" spacing={1}>
        {/* Always visible. The match count and Clear live here rather than with
            the controls, so a collapsed bar can never leave the table filtered
            with nothing on screen explaining why. */}
        <Stack direction="row" spacing={1} alignItems="center" useFlexGap sx={{ flexWrap: 'wrap', rowGap: 1 }}>
            <Button
                size="small"
                color={active ? 'primary' : 'inherit'}
                startIcon={<FilterAltIcon />}
                endIcon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                onClick={() => setExpanded(!expanded)}
            >
                <Localized id="urs-filter-toggle"><span>Filters</span></Localized>
            </Button>
            <Box sx={{ flex: 1 }} />
            {active && <>
                <Chip
                    size="small"
                    color={matchCount === 0 ? 'default' : 'primary'}
                    variant="outlined"
                    label={<Localized id="urs-filter-matches" vars={{ count: matchCount, total: totalCount }}>
                        <span>{`${matchCount} of ${totalCount}`}</span>
                    </Localized>}
                />
                <Button
                    size="small"
                    startIcon={<FilterAltOffIcon />}
                    disabled={disabled}
                    onClick={handleClear}
                >
                    <Localized id="clear"><span>Clear</span></Localized>
                </Button>
            </>}
            {disabled && <Chip
                size="small"
                color="warning"
                variant="outlined"
                label={<Localized id="urs-filter-reorder-hint">
                    <span>Finish or cancel reordering to filter</span>
                </Localized>}
            />}
        </Stack>
        <Collapse in={expanded} unmountOnExit>
            <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                useFlexGap
                sx={{ flexWrap: 'wrap', rowGap: 1 }}
            >
                <TextField
                    size="small"
                    sx={{ width: 220 }}
                    disabled={disabled}
                    value={text}
                    onChange={e => setText(e.target.value)}
                    label={<Localized id="search"><span>Search</span></Localized>}
                    InputProps={{
                        startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
                    }}
                />
                <TextField
                    size="small"
                    select
                    sx={{ width: 190 }}
                    disabled={disabled}
                    value={filter.comments}
                    onChange={e => handleSet('comments', e.target.value as CommentsFilter)}
                    label={<Localized id="comments"><span>Comments</span></Localized>}
                >
                    <MenuItem value="all"><Localized id="urs-filter-comments-all"><span>All</span></Localized></MenuItem>
                    <MenuItem value="with"><Localized id="urs-filter-comments-with"><span>With comments</span></Localized></MenuItem>
                    <MenuItem value="unresolved"><Localized id="urs-filter-comments-unresolved"><span>Unresolved</span></Localized></MenuItem>
                </TextField>
                <FacetSelect
                    label={<Localized id="urs-item-criticality"><span>Criticality</span></Localized>}
                    options={CRITICALITIES}
                    selected={filter.criticality}
                    onChange={values => handleSetChange('criticality', values)}
                    disabled={disabled}
                />
                <FacetSelect
                    label={<Localized id="urs-filter-status"><span>Status</span></Localized>}
                    options={STATUSES}
                    selected={filter.status}
                    onChange={values => handleSetChange('status', values)}
                    disabled={disabled}
                />
                {showSupplierNote && <FacetSelect
                    label={<Localized id="urs-item-note"><span>Note</span></Localized>}
                    options={NOTES}
                    selected={filter.note as Set<string>}
                    onChange={values => handleSetChange('note', values)}
                    disabled={disabled}
                />}
                {/* A checkbox rather than a select: the facet is a plain yes/no, and
                    a two-option dropdown makes the reader open it to learn that. */}
                <FormControlLabel
                    sx={{ ml: .5, '& .MuiFormControlLabel-label': { fontSize: '0.875rem' } }}
                    control={<Checkbox
                        size="small"
                        disabled={disabled}
                        checked={filter.attachments === 'with'}
                        onChange={e => handleSet('attachments', e.target.checked ? 'with' : 'all')}
                    />}
                    label={<Localized id="urs-filter-attachments-with"><span>With attachments</span></Localized>}
                />
            </Stack>
        </Collapse>
    </Stack>
}
